import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpService } from './http.service';
import { SessionService } from './session.service';
import { environment } from 'src/environments/environment';
import { JsencryptService } from './jsencrypt.service';
import { SecurityService } from './security.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private user$: null | any;
  private authSubject$ = new BehaviorSubject(false);
  private fetchAuthSubject$ = new BehaviorSubject(false);

  constructor(
    private httpService: HttpService,
    private sessionService: SessionService,
    private jsEncrypt: JsencryptService,
    private securityService: SecurityService
  ) {
    this.isLogin();
  }

  get user() {
    if (this.user$) {
      return this.user$;
    } else {
      return null;
    }
  }

  get token() {
    if (this.user) {
      return this.user.token;
    } else {
      return null;
    }
  }

  get id() {
    if (this.user && this.user.data) {
      return this.user.data.id;
    } else {
      return null;
    }
  }

  get applicationNumber() {
    if (this.user && this.user.data) {
      return this.user.data.application_number;
    } else {
      return null;
    }
  }

  get udid() {
    if (this.user && this.user.data) {
      return this.user.data.udid_number;
    } else {
      return null;
    }
  }

  get hasLogin(): Observable<boolean> {
    return this.authSubject$.asObservable();
  }

  get hasUpdate(): Observable<boolean> {
    return this.fetchAuthSubject$.asObservable();
  }

  isAuthenticated(): boolean {
    return this.authSubject$.value;
  }

  async login(params: any): Promise<any> {
    return await this.httpService.post('login', params)
      .toPromise()
      .then(async (response: any) => {
        if (response._resultflag) {
          response._result.data = JSON.parse(this.securityService.aesDecrypt(response._result.data));
          return await this.sessionService.set(environment.auth.key, response._result)
            .then((res: any) => {
              this.authSubject$.next(true);
              this.user$ = (res) ? response._result : null;
              return response;
            });
        } else {
          return response;
        }
      });
  }

  sendMobileLoginOtp(params: any): Promise<any> {
    return this.httpService.post('pwdwithmobilelogin', params, { responseType: 'text' }).toPromise();
  }

  sendAadhaarSeedingOtp(params:any): Promise<any> {
    return this.httpService.post('loginwithaadhaar', params , { responseType: 'text' }).toPromise();
  }

  async aadhaarSeedingOtpLogin(params: any): Promise<any> {
    return await this.httpService.post('aadhaarloginseedingOtp', params, { responseType: 'text' })
      .toPromise()
      .then(async (response: any) => {
        if (typeof response === 'string') {
          try {
            let rawString = response.trim();
            if (rawString.startsWith('"') && rawString.endsWith('"')) {
              rawString = rawString.slice(1, -1);
            }
            if (rawString.startsWith('{')) {
              response = JSON.parse(rawString);
            } else {
              const dec = this.securityService.aesDecrypt(rawString);
              response = dec ? JSON.parse(dec) : response;
            }
          } catch (e) {}
        }
        if (response._resultflag) {
          const encryptedUserData = response?._result?.data;
          if (encryptedUserData) {
            const decryptedUserData = this.securityService.aesDecrypt(encryptedUserData);
            if (decryptedUserData) {
              response._result.data = JSON.parse(decryptedUserData);
            }
          }
          return await this.sessionService.set(environment.auth.key, response._result)
            .then((res: any) => {
              this.authSubject$.next(true);
              this.user$ = res ? response._result : null;
              return response;
            });
        }
        return response;
      });
  }

  async loginWithMobileOtp(params: any): Promise<any> {
    return await this.httpService.post('pwdotplogin', params, { responseType: 'text' })
      .toPromise()
      .then(async (response: any) => {
        if (typeof response === 'string') {
          try {
            let rawString = response.trim();
            if (rawString.startsWith('"') && rawString.endsWith('"')) {
              rawString = rawString.slice(1, -1);
            }
            if (rawString.startsWith('{')) {
              response = JSON.parse(rawString);
            } else {
              const dec = this.securityService.aesDecrypt(rawString);
              response = dec ? JSON.parse(dec) : response;
            }
          } catch (e) {}
        }
        if (response._resultflag) {
          const encryptedUserData = response?._result?.data;
          if (encryptedUserData) {
            const decryptedUserData = this.securityService.aesDecrypt(encryptedUserData);
            if (decryptedUserData) {
              response._result.data = JSON.parse(decryptedUserData);
            }
          }
          return await this.sessionService.set(environment.auth.key, response._result)
            .then((res: any) => {
              this.authSubject$.next(true);
              this.user$ = res ? response._result : null;
              return response;
            });
        }
        return response;
      });
  }

  async loginWithAadhaarOtp(params: any): Promise<any> {
    return await this.httpService.post('aadhaarOtpLogin', params)
      .toPromise()
      .then(async (response: any) => {
        if (response._resultflag) {
          response._result.data = JSON.parse(this.securityService.aesDecrypt(response._result.data));
          return await this.sessionService.set(environment.auth.key, response._result)
            .then((res: any) => {
              this.authSubject$.next(true);
              this.user$ = (res) ? response._result : null;
              return response;
            });
        } else {
          return response;
        }
      });
  }

  async registration(params: any): Promise<any> {
    return await this.httpService.post('register', params)
      .toPromise()
      .then(async (response: any) => {
        if (response.status) {
          return await this.sessionService.set(environment.auth.key, response.data)
            .then((res: any) => {
              this.authSubject$.next(true);
              this.user$ = (res) ? response.data : null;
              return response;
            });
        } else {
          return response;
        }
      });
  }


  async logout(): Promise<any> {
    return await this.sessionService.delete(environment.auth.key)
      .then(() => {
        this.authSubject$.next(false);
        this.user$ = null;
        return true;
      });
  }

  async $user(): Promise<any> {
    return await this.sessionService.get(environment.auth.key)
      .then((res: any) => {
        this.user$ = (res !== null) ? res : null;
        return (res !== null) ? res : null;
      });
  }

  async isLogin(): Promise<any> {
    return this.sessionService.get(environment.auth.key)
      .then((res: any) => {
        const isLogin = (res) ? true : false;
        this.user$ = (isLogin) ? res : null;
        this.authSubject$.next(isLogin);
        return isLogin;
      });
  }

  async fetchUser(): Promise<any> {
    if (this.applicationNumber) {
      const params = {
        application_number: this.securityService.aesEncrypt(this.applicationNumber)
      };
      return await this.httpService.post('getUpdateDetail', params)
        .toPromise()
        .then(async (response: any) => {
          if (response._resultflag) {
            response._result.data = JSON.parse(this.securityService.aesDecrypt(response._result.data));
            return await this.sessionService.set(environment.auth.key, response._result)
              .then((res: any) => {
                this.user$ = (res) ? response._result : null;
                this.authSubject$.next(true);
                this.fetchAuthSubject$.next(true);
                return response;
              });
          } else {
            return response;
          }
        });
    } else {
      return false;
    }
  }

}
